import React, { useState, useContext, createContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

const ConfirmContext = createContext(null);

export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
    return ctx;
}

export function ConfirmProvider({ children }) {
    const [options, setOptions] = useState(null);
    const [resolver, setResolver] = useState(null);
    const cancelBtnRef = useRef(null);

    const confirm = (opts) => {
        setOptions(opts || {});
        return new Promise((resolve) => setResolver(() => resolve));
    };

    const handleClose = (result) => {
        if (resolver) resolver(!!result);
        setResolver(null);
        setOptions(null);
    };

    // Esc to close
    useEffect(() => {
        if (!options) return;
        const onKey = (e) => { if (e.key === "Escape") handleClose(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [options]);

    // Autofocus + lock body scroll while open
    useEffect(() => {
        if (!options) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        // slight delay so element exists in DOM
        const id = requestAnimationFrame(() => cancelBtnRef.current?.focus());
        return () => {
            document.body.style.overflow = prevOverflow;
            cancelAnimationFrame(id);
        };
    }, [options]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            {createPortal(
                <AnimatePresence>
                    {options && (
                        <>
                            {/* overlay */}
                            <motion.div
                                key="confirm-overlay"
                                className="fixed inset-0 z-50 bg-black/20"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.18, ease: "easeOut" }}
                                onMouseDown={() => handleClose(false)}
                            />

                            {/* centering container */}
                            <div
                                className="fixed inset-0 z-[60] grid place-items-center pointer-events-none"
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                {/* dialog */}
                                <motion.div
                                    key="confirm-dialog"
                                    className="pointer-events-auto w-[92vw] max-w-md
                       bg-white rounded-md shadow-lg border border-gray-200 p-4"
                                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                                    transition={{ duration: 0.18, ease: "easeOut" }}
                                    role="dialog"
                                    aria-modal="true"
                                    aria-label={options.title || "Confirm"}
                                >
                                    <h2 className="text-base font-semibold mb-2 text-gray-900">
                                        {options.title || "Confirm"}
                                    </h2>
                                    <p className="text-sm text-gray-700 mb-4">{options.message}</p>
                                    <div className="flex justify-end gap-2">
                                        <button
                                            ref={cancelBtnRef}
                                            onClick={() => handleClose(false)}
                                            className="px-4 py-2 rounded text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-300"
                                        >
                                            {options.cancelText || "Cancel"}
                                        </button>
                                        <button
                                            onClick={() => handleClose(true)}
                                            className="px-4 py-2 rounded text-sm font-medium text-white bg-[#7F56D9] hover:bg-[#8061E6] focus:outline-none focus:ring-2 focus:ring-violet-300"
                                        >
                                            {options.confirmText || "OK"}
                                        </button>
                                    </div>
                                </motion.div>
                            </div>
                        </>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </ConfirmContext.Provider>
    );
}
